import React, { useEffect, useRef, useState } from "react";
import {
  deletePayment,
  EbayPayment,
  fetchFromEbayPaymentSettings,
  fetchPaymentSettings,
  updatePayment,
} from "../../../api/rest/ebay";
import Loader from "../../../components/generic/Loader";
import EbayDeleteSetting from "../../../components/integrations/EbayDeleteSetting";
import EbayErrorComponent from "../../../components/integrations/EbayErrorComponent";
import EbayPaymentMethodsForm from "../../../components/integrations/EbayPaymentMethodsForm";
import EbaySettingsList from "../../../components/integrations/EbaySettingsList";
import { useStoreDetails } from "../../../hooks/storeHooks";
import { useQueryState } from "../../../hooks/stateHooks";
import { parseEbayErrorMessage } from "../../../utils/integrations";
import { segmentAnalytics } from "../../../components/services/Analytics";
import { SAVE_POLICY_CLICKED } from "../../../constants/eventsTracked";

const PAYMENT_SETTINGS = "Payment Settings";

const wrapTitle = (children: React.ReactChild, errors: string[]) => (
  <>
    <h2>{PAYMENT_SETTINGS}</h2>
    <EbayErrorComponent errors={errors} />
    {children}
  </>
);

function PaymentSettingsContainer() {
  const { customerId } = useStoreDetails();
  const listRef = useRef<HTMLElement>();
  const [settings, setSettings] = useState<EbayPayment[]>([]);
  const [editSetting, setEditSetting] = useQueryState<number | null>(
    "edit",
    null
  );
  const [deletingSetting, setDeletingSetting] = useQueryState<number | null>(
    "delete",
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const loadSettings = () => {
    setIsLoading(true);
    fetchPaymentSettings()
      .then((data: EbayPayment[]) => setSettings(data))
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)))
      .finally(() => setIsLoading(false));
  };

  const refreshSettings = () => {
    setIsLoading(true);
    fetchFromEbayPaymentSettings()
      .then((data: EbayPayment[]) => setSettings(data))
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)))
      .finally(() => setIsLoading(false));
  };

  const saveSetting = (values: EbayPayment, setSubmitting: Function) => {
    if (values.id < 0) values.id = null;
    setSubmitting(true);
    updatePayment(values)
      .then(() => {
        segmentAnalytics.track(SAVE_POLICY_CLICKED, {
          integration_type: "ebay",
          policy_type: PAYMENT_SETTINGS,
        });
        loadSettings();
        setEditSetting(null);
      })
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)))
      .finally(() => setSubmitting(false));
  };

  const deleteSetting = (settingId: number) => {
    setIsDeleting(true);
    deletePayment(settingId)
      .then(() => loadSettings())
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)))
      .finally(() => {
        setIsDeleting(false);
        setDeletingSetting(null);
      });
  };

  useEffect(() => {
    if (customerId) {
      loadSettings();
    }
  }, [customerId]);

  useEffect(() => {
    setErrors([]);
  }, [editSetting, deletingSetting]);

  if (isLoading) {
    return wrapTitle(<Loader />, errors);
  }

  if (editSetting) {
    return wrapTitle(
      <EbayPaymentMethodsForm
        paymentSetting={settings.find((setting) => setting.id === editSetting)}
        setEditSetting={() => setEditSetting(null)}
        saveSetting={saveSetting}
      />,
      errors
    );
  }

  if (deletingSetting) {
    const location = settings.find((setting) => setting.id === deletingSetting);
    if (location) {
      const { id, name } = location;
      return wrapTitle(
        <EbayDeleteSetting
          id={id}
          name={name}
          isDeleting={isDeleting}
          handleCancel={() => setDeletingSetting(null)}
          handleDelete={(deleteId: number) => deleteSetting(deleteId)}
        />,
        errors
      );
    }
  }

  return wrapTitle(
    <EbaySettingsList
      settingsTitle={PAYMENT_SETTINGS}
      settings={settings}
      deletingSetting={deletingSetting}
      refreshSettings={refreshSettings}
      setEditSetting={setEditSetting}
      setDeletingSetting={setDeletingSetting}
      listRef={listRef}
    />,
    errors
  );
}

export default PaymentSettingsContainer;
